import { Inject, Injectable } from '@angular/core';
import {
  MasterDatasetActivityRecord,
  MasterDatasetActivityKeys,
  MasterDatasetCrudActivityEntry,
  MasterDatasetCrudActivityEntryKeys,
  MasterDatasetType,
  MasterDatasetUploadActivityEntry,
  MasterDatasetUploadActivityEntryKeys,
  REPORTING_MASTER_DATASET_ACTIVITY_COLLECTION,
  REPORTING_SIGNALS_COLLECTION,
} from '@ag-common-lib/lib/models/reporting/master-dataset.model';
import { REPORTING_COLLECTION } from '@ag-common-lib/lib/models/reporting/reporting-db.model';
import { FirebaseApp } from 'firebase/app';
import { CommonFireStoreDao } from '../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../injections/firebase-app';
import { filter, map, Observable } from 'rxjs';
import { dateFromTimestamp } from '../utils/date-from-timestamp';

@Injectable({ providedIn: 'root' })
export class MasterDatasetActivityService {
  private readonly fsDao: CommonFireStoreDao<Record<string, unknown>>;

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    this.fsDao = new CommonFireStoreDao<Record<string, unknown>>(fireBaseApp, null, null);
  }

  getActivity(datasetType: MasterDatasetType): Observable<MasterDatasetActivityRecord | null> {
    return this.fsDao
      .getDocument(this.getActivityCollectionPath(), datasetType)
      .pipe(
        map(snapshot =>
          this.mapSnapshot(datasetType, snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null),
        ),
      );
  }

  getLastModifiedDate(datasetType: MasterDatasetType): Observable<Date | null> {
    return this.getActivity(datasetType).pipe(map(activity => this.resolveLatestActivityDate(activity)));
  }

  getLastUploadDate(datasetType: MasterDatasetType): Observable<Date | null> {
    return this.fsDao.getDocument(this.getActivityCollectionPath(), datasetType).pipe(
      filter(snapshot => snapshot.exists() || !(snapshot as any).metadata?.fromCache),
      map(snapshot =>
        this.resolveLatestUploadDate(
          this.mapSnapshot(datasetType, snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null),
        ),
      ),
    );
  }

  private mapSnapshot(
    datasetType: MasterDatasetType,
    data: Record<string, unknown> | null,
  ): MasterDatasetActivityRecord | null {
    if (!data) {
      return null;
    }

    const uploadByYearRaw = asRecord(data[MasterDatasetActivityKeys.latestUploadByYear]);
    const uploadByYear = Object.keys(uploadByYearRaw).reduce<Record<string, MasterDatasetUploadActivityEntry>>(
      (accumulator, year) => {
        const value = asRecord(uploadByYearRaw[year]);
        const reportDateRaw = String(value[MasterDatasetUploadActivityEntryKeys.reportDate] ?? '').trim();
        const runIdRaw = String(value[MasterDatasetUploadActivityEntryKeys.runId] ?? '').trim();
        const uploadActionIdRaw = String(value[MasterDatasetUploadActivityEntryKeys.uploadActionId] ?? '').trim();
        const sourceFileNameRaw = String(value[MasterDatasetUploadActivityEntryKeys.sourceFileName] ?? '').trim();
        const sourceFilePathRaw = String(value[MasterDatasetUploadActivityEntryKeys.sourceFilePath] ?? '').trim();
        const statusRaw = String(value[MasterDatasetUploadActivityEntryKeys.status] ?? '').trim();
        const recordsTotalRaw = Number(value[MasterDatasetUploadActivityEntryKeys.recordsTotal]);
        const recordsValidRaw = Number(value[MasterDatasetUploadActivityEntryKeys.recordsValid]);
        const recordsInvalidRaw = Number(value[MasterDatasetUploadActivityEntryKeys.recordsInvalid]);
        const uploadUpdatedAtRaw = normalizeActivityDate(value[MasterDatasetUploadActivityEntryKeys.updatedAt]);

        accumulator[year] = {
          [MasterDatasetUploadActivityEntryKeys.reportDate]: reportDateRaw || null,
          [MasterDatasetUploadActivityEntryKeys.runId]: runIdRaw || null,
          [MasterDatasetUploadActivityEntryKeys.uploadActionId]: uploadActionIdRaw || null,
          [MasterDatasetUploadActivityEntryKeys.sourceFileName]: sourceFileNameRaw || null,
          [MasterDatasetUploadActivityEntryKeys.sourceFilePath]: sourceFilePathRaw || null,
          [MasterDatasetUploadActivityEntryKeys.status]: statusRaw || null,
          [MasterDatasetUploadActivityEntryKeys.recordsTotal]: Number.isFinite(recordsTotalRaw)
            ? recordsTotalRaw
            : null,
          [MasterDatasetUploadActivityEntryKeys.recordsValid]: Number.isFinite(recordsValidRaw)
            ? recordsValidRaw
            : null,
          [MasterDatasetUploadActivityEntryKeys.recordsInvalid]: Number.isFinite(recordsInvalidRaw)
            ? recordsInvalidRaw
            : null,
          [MasterDatasetUploadActivityEntryKeys.updatedAt]: uploadUpdatedAtRaw,
        };
        return accumulator;
      },
      {},
    );

    const latestCrudRaw = asRecord(data[MasterDatasetActivityKeys.latestCrudUpdate]);
    const latestCrud: MasterDatasetCrudActivityEntry | null = Object.keys(latestCrudRaw).length
      ? {
          [MasterDatasetCrudActivityEntryKeys.operation]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.operation] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.rowKey]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.rowKey] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.uid]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.uid] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.email]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.email] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.service]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.service] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.changeLogId]:
            String(latestCrudRaw[MasterDatasetCrudActivityEntryKeys.changeLogId] ?? '').trim() || null,
          [MasterDatasetCrudActivityEntryKeys.updatedAt]: normalizeActivityDate(
            latestCrudRaw[MasterDatasetCrudActivityEntryKeys.updatedAt],
          ),
        }
      : null;

    const datasetUpdatedAtRaw = normalizeActivityDate(data[MasterDatasetActivityKeys.updatedAt]);

    return {
      datasetType,
      updatedAt: datasetUpdatedAtRaw,
      latestUploadByYear: uploadByYear,
      latestCrudUpdate: latestCrud,
    };
  }

  private resolveLatestActivityDate(activity: MasterDatasetActivityRecord | null): Date | null {
    if (!activity) {
      return null;
    }

    const candidateDates: Date[] = [];
    pushDate(candidateDates, activity.updatedAt);
    pushDate(candidateDates, activity.latestCrudUpdate?.updatedAt);
    Object.values(activity.latestUploadByYear).forEach(upload => pushDate(candidateDates, upload.updatedAt));

    if (!candidateDates.length) {
      return null;
    }

    return candidateDates.reduce((latest, current) => (current.getTime() > latest.getTime() ? current : latest));
  }

  private resolveLatestUploadDate(activity: MasterDatasetActivityRecord | null): Date | null {
    if (!activity) {
      return null;
    }

    const candidateDates: Date[] = [];
    Object.values(activity.latestUploadByYear).forEach(upload => pushDate(candidateDates, upload.updatedAt));
    pushDate(candidateDates, activity.updatedAt);

    if (!candidateDates.length) {
      return null;
    }

    return candidateDates.reduce((latest, current) => (current.getTime() > latest.getTime() ? current : latest));
  }

  private getActivityCollectionPath(): string {
    return [REPORTING_COLLECTION, REPORTING_SIGNALS_COLLECTION, REPORTING_MASTER_DATASET_ACTIVITY_COLLECTION].join('/');
  }
}

function pushDate(bucket: Date[], value: Date | null | undefined): void {
  if (!value || Number.isNaN(value.getTime())) {
    return;
  }

  bucket.push(value);
}

function normalizeActivityDate(value: unknown): Date | null {
  const normalizedDate = dateFromTimestamp(value);
  if (isFiniteDate(normalizedDate)) {
    return normalizedDate;
  }

  if (value && typeof value === 'object') {
    const timestampValue = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof timestampValue.toDate === 'function') {
      const date = timestampValue.toDate();
      if (isFiniteDate(date)) {
        return date;
      }
    }

    const seconds = Number(timestampValue.seconds ?? timestampValue._seconds);
    if (Number.isFinite(seconds)) {
      return new Date(seconds * 1000);
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);
    if (isFiniteDate(parsedDate)) {
      return parsedDate;
    }
  }

  return null;
}

function isFiniteDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return value as Record<string, unknown>;
}
